import Lenis from 'lenis'
import { animate, stagger, inView } from 'motion'
import ease from '@/utils/ease'
import { getLenis } from '@/lib/lenis'
import 'keen-slider/keen-slider.min.css'
import KeenSlider from 'keen-slider'

let galleryLenis = null
let sizeGuideLenis = null

export default () => ({
	sliderInstances: [],
	modalOpen: false,
	galleryOpen: false,
	sizeGuideOpen: false,
	currentMediaIndex: 0,
	modalSliderInstance: null,
	currentlyOpenedDrawer: null,
	selectedOptions: {},
	variants: [],
	currentVariant: null,

	init() {
		this.animateLines()
		this.animateVariants()
		this.initCarousels()
		this.setupSellingPlans()
		this.initModalSlider()
		this.initVariantSelector()

		this.$watch('galleryOpen', (open) => {
			if (open) {
				getLenis()?.stop()
				requestAnimationFrame(() => {
					this.initGalleryLenis()
				})
			} else {
				this.destroyGalleryLenis()
				getLenis()?.start()
			}
		})

		this.$watch('sizeGuideOpen', (open) => {
			if (open) {
				this.openSizeGuide()
			} else {
				this.closeSizeGuide()
			}
		})
	},

	getSizeGuideEl() {
		if (!this.sizeGuideEl) {
			this.sizeGuideEl = document.querySelector('[data-size-guide-drawer]')
		}
		return this.sizeGuideEl
	},

	initVariantSelector() {
		const productJsonEl = this.$el.querySelector('[x-ref="productJson"]')
		if (!productJsonEl) return

		try {
			const productData = JSON.parse(productJsonEl.textContent || '{}')
			this.variants = productData.variants || []
			this.selectedOptions = productData.selectedOptions || {}

			// Find initial variant
			this.updateCurrentVariant()
		} catch (e) {
			console.error('Failed to parse product JSON for variants', e)
		}
	},

	selectOption(optionName, value) {
		this.selectedOptions[optionName] = value
		this.updateCurrentVariant()
	},

	isOptionAvailable(optionName, value) {
		// Check if selecting this option value would result in any available variant
		// given the current selections for other options
		const testOptions = { ...this.selectedOptions, [optionName]: value }

		return this.variants.some((variant) => {
			// Check if this variant matches the test options
			const matches = variant.options.every((opt) => {
				// If we have a selection for this option, it must match
				if (testOptions[opt.name] !== undefined) {
					return testOptions[opt.name] === opt.value
				}
				return true
			})
			// Return true if variant matches AND is available
			return matches && variant.available
		})
	},

	getOptionClass(optionName, value) {
		const isSelected = this.selectedOptions[optionName] === value
		const isAvailable = this.isOptionAvailable(optionName, value)

		if (isSelected) {
			return 'text-black'
		}
		if (!isAvailable) {
			return 'text-soft-gray opacity-50 cursor-not-allowed line-through'
		}
		return 'text-soft-gray hover:text-black'
	},

	updateCurrentVariant() {
		// Find the variant that matches all selected options
		const matchingVariant = this.variants.find((variant) => {
			return variant.options.every((opt) => {
				return this.selectedOptions[opt.name] === opt.value
			})
		})

		if (matchingVariant) {
			this.currentVariant = matchingVariant
			// Update the hidden variant input in the form
			const variantInput = this.$el.querySelector('input[name="id"]')
			if (variantInput) {
				variantInput.value = matchingVariant.id
				variantInput.disabled = !matchingVariant.available
			}
			// Update URL without page reload
			const url = new URL(window.location.href)
			url.searchParams.set('variant', matchingVariant.id)
			window.history.replaceState({}, '', url)
		}
	},

	initGalleryLenis() {
		const wrapper = document.querySelector('[data-gallery-scroll]')
		if (!wrapper || galleryLenis) return

		galleryLenis = new Lenis({
			wrapper,
			content: wrapper,
			smoothWheel: true,
			lerp: 0.1,
		})

		const raf = (time) => {
			if (galleryLenis) {
				galleryLenis.raf(time)
				requestAnimationFrame(raf)
			}
		}
		requestAnimationFrame(raf)
	},

	destroyGalleryLenis() {
		if (galleryLenis) {
			galleryLenis.destroy()
			galleryLenis = null
		}
	},

	openSizeGuide() {
		const el = this.getSizeGuideEl()
		getLenis()?.stop()

		if (el) {
			animate(
				el,
				{ transform: 'translateX(0)' },
				{
					duration: 0.3,
					easing: ease.outQuint,
				},
			)
		}

		requestAnimationFrame(() => {
			this.initSizeGuideLenis()
		})
	},

	closeSizeGuide() {
		const el = this.getSizeGuideEl()
		this.destroySizeGuideLenis()
		getLenis()?.start()

		if (el) {
			animate(
				el,
				{ transform: 'translateX(100%)' },
				{
					duration: 0.3,
					easing: ease.outQuint,
				},
			)
		}
	},

	initSizeGuideLenis() {
		const wrapper = this.getSizeGuideEl()?.querySelector('.size-guide__content')
		if (!wrapper || sizeGuideLenis) return

		sizeGuideLenis = new Lenis({
			wrapper,
			content: wrapper,
			smoothWheel: true,
			lerp: 0.1,
		})

		const raf = (time) => {
			if (sizeGuideLenis) {
				sizeGuideLenis.raf(time)
				requestAnimationFrame(raf)
			}
		}
		requestAnimationFrame(raf)
	},

	destroySizeGuideLenis() {
		if (sizeGuideLenis) {
			sizeGuideLenis.destroy()
			sizeGuideLenis = null
		}
	},

	animateLines() {
		const horizontalLines = this.$el.querySelectorAll('[data-horizontal-line]')
		const verticalLines = this.$el.querySelectorAll('[data-vertical-line]')

		if (horizontalLines?.length) {
			inView(
				this.$el,
				() =>
					animate(
						horizontalLines,
						{ scaleX: 1 },
						{ duration: 0.4, delay: 0.2, easing: ease.inOutQuint },
					),
				{ amount: 'some' },
			)
		}
		if (verticalLines?.length) {
			inView(
				this.$el,
				() =>
					animate(
						verticalLines,
						{ scaleY: 1 },
						{ duration: 0.4, delay: 0.2, easing: ease.inOutQuint },
					),
				{ amount: 'some' },
			)
		}
	},

	animateVariants() {
		const variants = this.$el.querySelectorAll('[data-animate-variant]')

		if (!variants) return

		setTimeout(() => {
			inView(
				this.$el,
				() =>
					animate(
						variants,
						{ opacity: 1 },
						{ duration: 0.4, delay: stagger(0.075), easing: ease.inOutQuint },
					),
				{ amount: 'some' },
			)
		}, 200)
	},

	initCarousels() {
		const carousels = this.$el.querySelectorAll('.keen-slider')
		if (!carousels?.length) return

		// Wait for next frame to ensure layout is complete
		requestAnimationFrame(() => {
			carousels.forEach((slider) => {
				const slideCount = slider.querySelectorAll('.keen-slider__slide').length
				if (slideCount <= 1) return

				const sliderConfig = {
					slides: { perView: 1, spacing: 0 },
					loop: true,
					created: (s) => {
						this.createSlideCounter(s)
					},
					slideChanged: (s) => {
						this.updateSlideCounter(s)
					},
				}

				const instance = new KeenSlider(slider, sliderConfig)
				this.sliderInstances.push(instance)
			})
		})
	},

	createSlideCounter(slider) {
		const wrapper = slider.container.parentNode
		const counter = document.createElement('div')
		counter.classList.add('slide-counter', 'absolute', 'bottom-16', 'right-16')
		counter.style.cssText =
			'font-family: "STKBureau-Serif", serif; font-size: 12px; font-weight: 400; letter-spacing: 0.12px; color: #000; text-align: right; font-variant-numeric: normal; font-feature-settings: "frac" 0;'
		const total = slider.track.details.slides.length
		counter.textContent = `1\u2009/\u2009${total}`
		wrapper.appendChild(counter)
	},

	updateSlideCounter(slider) {
		const wrapper = slider.container.parentNode
		const counter = wrapper.querySelector('.slide-counter')
		if (!counter) return

		const current = slider.track.details.rel + 1
		const total = slider.track.details.slides.length
		counter.textContent = `${current}\u2009/\u2009${total}`
	},

	setupSellingPlans() {
		const sellingPlansJsonEl = this.$el.querySelector('[x-ref="sellingPlansJson"]')
		const optionsContainer = this.$el.querySelector('#selling-plan-options')
		const priceEl = this.$el.querySelector('#selling-plan-price')
		const disclaimerEl = this.$el.querySelector('#disclaimer-text')
		if (!sellingPlansJsonEl || !optionsContainer || !priceEl) return

		let data: any = {}
		try {
			data = JSON.parse(sellingPlansJsonEl.textContent || '{}')
		} catch (e) {
			console.error('Failed to parse selling plans JSON', e)
			return
		}

		const plans = data.plans || []
		const variantPrice = data.variant_price || 0

		// Calculate actual prices for each plan
		plans.forEach((plan) => {
			const adjustment = plan.price_adjustments?.[0]
			let calculatedPrice = variantPrice

			if (adjustment && adjustment.value_type === 'percentage') {
				const discount = (variantPrice * adjustment.value) / 100
				calculatedPrice = variantPrice - discount
			}

			// Format price as currency
			plan.calculatedPrice = `$${(calculatedPrice / 100).toFixed(2)}`
		})

		// Sort plans numerically by extracting the number from the name
		plans.sort((a, b) => {
			const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0', 10)
			const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0', 10)
			return aNum - bNum
		})

		let selectedPlanId = plans[0]?.id

		// Get the full price (subscription/no discount price) for comparison
		const fullPricePlan = plans.find((plan) => plan.group_name === 'Subscription') || plans[0]
		const fullPrice = fullPricePlan?.calculatedPrice || ''

		// Helper function to format plan name and calculate discount
		function formatPlanDisplay(plan) {
			const number = plan.name.match(/\d+/)?.[0] || '1'
			const displayName = number === '1' ? 'Every month' : `Every ${number} months`

			// Calculate discount percentage if this is a prepaid plan
			let discountBadge = ''
			if (plan.group_name === 'Prepaid' && fullPricePlan) {
				const fullPriceNum = parseFloat(
					fullPricePlan.calculatedPrice.replace(/[^0-9.]/g, ''),
				)
				const planPriceNum = parseFloat(plan.calculatedPrice.replace(/[^0-9.]/g, ''))
				const discountPercent = Math.round(
					((fullPriceNum - planPriceNum) / fullPriceNum) * 100,
				)

				if (discountPercent > 0) {
					discountBadge = `<span class="inline-block h-18 rounded-8 border border-solid border-black px-6 font-mono text-10 leading-16 tracking-2 tabular-nums">Save ${discountPercent}%</span>`
				}
			}

			return `${displayName}${discountBadge}`
		}

		function render() {
			optionsContainer.innerHTML = plans
				.map(
					(plan) => `
				<label class="flex items-center gap-8 cursor-pointer body">
					<input
						type="radio"
						name="selling_plan"
						value="${plan.id}"
						${plan.id === selectedPlanId ? 'checked' : ''}
						class="accent-current"
					/>
					<span class="flex items-center gap-x-8">${formatPlanDisplay(plan)}</span>
				</label>
			`,
				)
				.join('')

			updatePrice()
			updateDisclaimer()
		}

		function updatePrice() {
			const selectedPlan = plans.find((p) => p.id === selectedPlanId)
			if (selectedPlan) {
				// If this is a prepaid plan (has discount), show current price with strikethrough
				if (selectedPlan.group_name === 'Prepaid') {
					priceEl.innerHTML = `
						<span class="h3">${selectedPlan.calculatedPrice}</span>
						<span class="text-black/30 line-through ml-8">${fullPrice}</span>
					`
				} else {
					// If this is subscription (full price), just show the price
					priceEl.innerHTML = `<span class="h3">${selectedPlan.calculatedPrice}</span>`
				}
			}
		}

		function updateDisclaimer() {
			const selectedPlan = plans.find((p) => p.id === selectedPlanId)
			if (selectedPlan && disclaimerEl) {
				const monthsNumber = parseInt(selectedPlan.name.match(/\d+/)?.[0] || '1', 10)
				const daysSupply = monthsNumber * 28 // 28 days per month
				const planPrice = selectedPlan.calculatedPrice

				let billingText = ''
				if (monthsNumber === 1) {
					billingText = `${planPrice} billed every month`
				} else {
					billingText = `${planPrice} billed every ${monthsNumber} months`
				}

				disclaimerEl.textContent = `${daysSupply} day supply. ${billingText}.`
			}
		}

		optionsContainer.addEventListener('change', (e) => {
			if (e.target.name === 'selling_plan') {
				selectedPlanId = parseInt(e.target.value, 10)
				updatePrice()
				updateDisclaimer()
			}
		})

		render()
	},

	initModalSlider() {
		// Store references to event handlers for cleanup
		this.modalArrowHandlers = {
			left: null,
			right: null,
		}
		this.modalDotHandlers = []

		this.$watch('modalOpen', (value) => {
			if (value && this.$refs.modalSlider && !this.modalSliderInstance) {
				// Opening modal - create slider immediately
				this.$nextTick(() => {
					if (typeof KeenSlider !== 'undefined') {
						this.modalSliderInstance = new KeenSlider(this.$refs.modalSlider, {
							initial: this.currentMediaIndex || 0,
							loop: true,
							defaultAnimation: { duration: 0 },
							created: (slider) => {
								if (this.currentMediaIndex !== undefined) {
									slider.moveToIdx(this.currentMediaIndex)
								}
								this.createModalDots(slider)
							},
							slideChanged: (slider) => {
								if (this.currentMediaIndex !== undefined) {
									this.currentMediaIndex = slider.track.details.rel
								}
								this.updateModalDots(slider)
							},
						})
					}
				})
			} else if (!value && this.modalSliderInstance) {
				// Closing modal - wait for transition to complete before destroying
				// Alpine's transition duration is 200ms for leave
				setTimeout(() => {
					if (this.modalSliderInstance) {
						this.modalSliderInstance.destroy()
						this.modalSliderInstance = null
						// Remove dots container and arrows with their event listeners
						const parent = this.$refs.modalSlider?.parentNode
						if (parent) {
							const dotsContainer = parent.querySelector('.dot-container')
							const arrowLeft = parent.querySelector('.arrow--left')
							const arrowRight = parent.querySelector('.arrow--right')

							// Remove arrow event listeners
							if (arrowLeft && this.modalArrowHandlers.left) {
								arrowLeft.removeEventListener('click', this.modalArrowHandlers.left)
								arrowLeft.remove()
							}
							if (arrowRight && this.modalArrowHandlers.right) {
								arrowRight.removeEventListener(
									'click',
									this.modalArrowHandlers.right,
								)
								arrowRight.remove()
							}

							// Remove dot event listeners
							const dots = parent.querySelectorAll('.dot')
							dots.forEach((dot, idx) => {
								if (this.modalDotHandlers[idx]) {
									dot.removeEventListener('click', this.modalDotHandlers[idx])
								}
							})

							if (dotsContainer) dotsContainer.remove()

							// Clear handler references
							this.modalArrowHandlers = { left: null, right: null }
							this.modalDotHandlers = []
						}
					}
				}, 250) // Slightly longer than transition duration to ensure completion
			}
		})
	},

	createModalDots(slider) {
		const wrapper = slider.container.parentNode
		const dotsContainer = document.createElement('div')
		dotsContainer.classList.add(
			'dot-container',
			'fixed',
			'bottom-[10vh]',
			'left-1/2',
			'-translate-x-1/2',
			'flex',
			'justify-center',
			'items-center',
			'gap-x-5',
		)
		wrapper.appendChild(dotsContainer)

		// Clear previous dot handlers
		this.modalDotHandlers = []

		slider.track.details.slides.forEach((_, idx) => {
			const dot = document.createElement('button')
			dot.classList.add(
				'dot',
				'h-5',
				'w-5',
				'border',
				'border-solid',
				'border-white',
				'cursor-pointer',
			)
			// Check against currentMediaIndex or current slider position
			if (idx === (this.currentMediaIndex || slider.track.details.rel || 0)) {
				dot.classList.add('dot--active', 'bg-white')
			}
			// Store handler reference for cleanup
			const handler = () => slider.moveToIdx(idx)
			this.modalDotHandlers.push(handler)
			dot.addEventListener('click', handler)
			dotsContainer.appendChild(dot)
		})

		// Add arrow navigation with stored handlers
		const arrowLeft = document.createElement('button')
		arrowLeft.classList.add('arrow', 'arrow--left', 'cursor-pointer')
		arrowLeft.innerHTML = `<svg width="13" height="24" viewBox="0 0 13 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M12 1L1 12L12 23" stroke="white"/>
		</svg>`
		this.modalArrowHandlers.left = () => slider.prev()
		arrowLeft.addEventListener('click', this.modalArrowHandlers.left)
		wrapper.appendChild(arrowLeft)

		const arrowRight = document.createElement('button')
		arrowRight.classList.add('arrow', 'arrow--right', 'cursor-pointer')
		arrowRight.innerHTML = `<svg width="13" height="24" viewBox="0 0 13 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M1 23L12 12L0.999998 1" stroke="white"/>
		</svg>`
		this.modalArrowHandlers.right = () => slider.next()
		arrowRight.addEventListener('click', this.modalArrowHandlers.right)
		wrapper.appendChild(arrowRight)
	},

	updateModalDots(slider) {
		const wrapper = slider.container.parentNode
		const dotContainer = wrapper.querySelector('.dot-container')
		if (!dotContainer) return

		const currentSlide = slider.track.details.rel
		const dotElements = dotContainer.querySelectorAll('.dot')

		dotElements.forEach((dot, idx) => {
			if (idx === currentSlide) {
				dot.classList.add('dot--active', 'bg-white')
			} else {
				dot.classList.remove('dot--active', 'bg-white')
			}
		})
	},

	toggleDrawer(drawerName) {
		const wasOpen = this.currentlyOpenedDrawer === drawerName
		const previousDrawer = this.currentlyOpenedDrawer

		// If there was a previously open drawer, animate it closing
		if (previousDrawer && previousDrawer !== drawerName) {
			const prevRef = this.$refs[previousDrawer]
			if (prevRef) {
				animate(prevRef, { height: '0px' }, { duration: 0, easing: ease.inOutQuint })
			}
		}

		// Toggle the current drawer
		if (wasOpen) {
			// Close the current drawer
			const currentRef = this.$refs[drawerName]
			if (currentRef) {
				animate(currentRef, { height: '0px' }, { duration: 0, easing: ease.inOutQuint })
			}
			this.currentlyOpenedDrawer = null
		} else {
			// Open the new drawer
			this.currentlyOpenedDrawer = drawerName
			this.$nextTick(() => {
				const currentRef = this.$refs[drawerName]
				const contentRef = this.$refs[drawerName + 'Content']
				if (currentRef && contentRef) {
					const targetHeight = contentRef.scrollHeight + 'px'
					animate(
						currentRef,
						{ height: targetHeight },
						{ duration: 0, easing: ease.inOutQuint },
					)
				}
			})
		}
	},
})
